import { ApiEntity, Entity, EntityId, TemporalEntityId } from 'entities-next/common'
import {
  NegativeOrganizationHeadquarters,
  OrganizationFoundedAt,
  OrganizationHeadquarters,
  OrganizationStages,
} from 'entities-next/scoutingCampaignCriteria'
import { Writeable } from 'types'

import { ScoutingCampaignStatus } from '../scoutingCampaign'

const MAX_HOURS_BETWEEN_BATCHES = 0
const MAX_PAIRINGS_PER_DAY = 2

export type APISearchFunction = SearchFunction & ApiEntity

export type SearchFunction = Entity & {
  readonly name: string
  readonly description: string
  readonly status: ScoutingCampaignStatus
  readonly editable: boolean
  readonly positiveKeywords: string
  readonly negativeKeywords: string
  readonly corporateId: EntityId
  readonly type?: SearchFunctionType
  readonly assignedOpsLeadId?: number
  readonly internalPositivePrimaryKeywordsSets?: string
  readonly internalPositiveSecondaryKeywords?: string
  readonly internalNegativePrimaryKeywords?: string
  readonly internalNegativeSecondaryKeywords?: string
  readonly internalCategories?: string
  readonly internalNegativeCategories?: string
  readonly internalLocations?: string
  readonly internalNegativeLocations?: string
  readonly internalStages?: string
  readonly internalMinimumYear?: number
  readonly internalMaximumYear?: number
  readonly internalIncludeCompaniesWithoutMaturityInformation: boolean
  readonly dailyCompanyLimit?: number
  readonly hoursBetweenBatchesOfPairings?: number
  readonly sampleCompanies?: string
  readonly sampleCompaniesText?: string[]
  readonly radarScouting: boolean
  readonly organizationHeadquarters?: OrganizationHeadquarters
  readonly negativeOrganizationHeadquarters?: NegativeOrganizationHeadquarters
  readonly organizationFoundedAt?: OrganizationFoundedAt
  readonly organizationStages?: OrganizationStages
  readonly relaxedSweetSpotDefinition?: string
  readonly includeCompaniesWithoutMaturityInformation: boolean
}

export enum SearchFunctionType {
  radarScouting = 'radar-scouting',
  sprintScouting = 'sprint-scouting',
  alwaysOnScouting = 'always-on-scouting',

  // Deprecated
  pacedScouting = 'paced-scouting',
  marketIntelligence = 'market-intelligence',
  earlyStageScouting = 'early-stage-scouting',
  precisionRadarRegularScouting = 'precision-radar-regular-scouting',
  industryRadarRegularScouting = 'industry-radar-regular-scouting',
  competitiveIntelligenceRegularScouting = 'competitive-intelligence-regular-scouting',
  competitiveIntelligenceSprintScouting = 'competitive-intelligence-sprint-scouting',
}

export const SEARCH_FUNCTION_CACHE_STORE_ID = 'searchFunctions'

export type SearchFunctionParams = Writeable<Partial<SearchFunction>> & {
  corporateId: EntityId
}

export function buildSearchFunction(searchFunctionsParams: SearchFunctionParams): SearchFunction {
  return {
    id: TemporalEntityId.new,
    name: '',
    description: '',
    status: ScoutingCampaignStatus.pendingValidation,
    editable: false,
    positiveKeywords: '',
    negativeKeywords: '',
    createdAt: new Date(),
    internalIncludeCompaniesWithoutMaturityInformation: false,
    internalPositivePrimaryKeywordsSets: '',
    internalPositiveSecondaryKeywords: '',
    internalNegativePrimaryKeywords: '',
    internalNegativeSecondaryKeywords: '',
    internalCategories: '',
    internalNegativeCategories: '',
    internalLocations: '',
    internalNegativeLocations: '',
    internalStages: '',
    internalMaximumYear: undefined,
    internalMinimumYear: undefined,
    dailyCompanyLimit: MAX_PAIRINGS_PER_DAY,
    hoursBetweenBatchesOfPairings: MAX_HOURS_BETWEEN_BATCHES,
    sampleCompanies: '',
    radarScouting: false,
    organizationHeadquarters: undefined,
    negativeOrganizationHeadquarters: undefined,
    organizationFoundedAt: undefined,
    organizationStages: undefined,
    relaxedSweetSpotDefinition: '',
    includeCompaniesWithoutMaturityInformation: true,
    ...searchFunctionsParams,
  }
}

export type SearchFunctionDto = Partial<
  Pick<
    SearchFunction,
    | 'name'
    | 'description'
    | 'status'
    | 'positiveKeywords'
    | 'negativeKeywords'
    | 'corporateId'
    | 'assignedOpsLeadId'
    | 'radarScouting'
    | 'organizationHeadquarters'
    | 'negativeOrganizationHeadquarters'
    | 'organizationFoundedAt'
    | 'organizationStages'
    | 'relaxedSweetSpotDefinition'
    | 'includeCompaniesWithoutMaturityInformation'
  >
>
